import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AskPanel } from './AskPanel';

/**
 * The chat, reachable from every tab.
 *
 * It used to live at the bottom of the Home tab, which meant you found it only
 * if you were already on Home and scrolled — while the questions people actually
 * ask it ("what is still owed?") come up on Vendors.
 *
 * **On a phone it opens as a full sheet, not a small window.** A 320px chat
 * bubble over a 390px screen is a worse version of both things. The button sits
 * above the home indicator, and hides itself while the sheet is open so it is
 * not a second close button in the corner of its own panel.
 */
export function AskLauncher({ wedId }: { wedId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Escape closes it and focus goes back to the button, so a keyboard user is
  // not dropped at the top of the document.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) panelRef.current?.querySelector('input')?.focus();
  }, [open]);

  return (
    <>
      {!open && (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded={false}
          /* Above the bottom bar on a phone, which is fixed and would otherwise
               sit on top of this button. Back to the corner from `sm` up, where
               there is no bottom bar. */
          className="fixed bottom-[calc(4.25rem+env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-40 inline-flex min-h-tap items-center gap-2 rounded-pill border border-leaf bg-leaf px-5 text-body text-cream transition-colors hover:bg-ink sm:bottom-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <Spark />
          {t('ask.title')}
        </button>
      )}

      {open && (
        <>
          {/* Dims the page on a phone, where the sheet covers it anyway, and
              gives a tap target to dismiss on a desktop. */}
          <div
            className="fixed inset-0 z-40 bg-ink/20"
            role="presentation"
            onClick={() => setOpen(false)}
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-label={t('ask.title')}
            className="panel fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col overflow-y-auto rounded-t px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:max-h-[36rem] sm:w-[28rem] sm:rounded"
          >
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  buttonRef.current?.focus();
                }}
                className="min-h-tap px-2 text-caption text-leaf-soft underline underline-offset-4"
              >
                {t('ask.close')}
              </button>
            </div>
            <AskPanel wedId={wedId} />
          </div>
        </>
      )}
    </>
  );
}

/** Four strokes, not a robot face. The app has one illustration and it is a fruit. */
function Spark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="size-4" fill="none">
      <path
        d="M8 1.5v3M8 11.5v3M1.5 8h3M11.5 8h3M3.4 3.4l2.1 2.1M10.5 10.5l2.1 2.1M12.6 3.4l-2.1 2.1M5.5 10.5l-2.1 2.1"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
